import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ApiHelperFunction from "../../api";
import { initialStateProps } from "./interface";
import { encodeStorageKey, toStorageKey } from "../../utils/fileUrl";

// ✅ Add User  Thunk
export const addClient = createAsyncThunk(
  "admin/create-client",
  async (payload: any, { dispatch, rejectWithValue }) => {
    try {
      const token = localStorage.getItem("user_token");

      const response = await ApiHelperFunction({
        url: "admin/create-client/",
        method: "post",
        data: payload,
        dispatch,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `${token}` : "",
        },
      });

      return response?.data;
    } catch (error: any) {
      

      return rejectWithValue(error);
    }
  },
);

// ✅ Update User Thunk
export const updateClient = createAsyncThunk(
  "admin/update-client",
  async (
    { id, ...payload }: { id: number; [key: string]: any },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `admin/update-client/${id}`,
      method: "put",
      data: payload,
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data;
  },
);

// ✅ Delete User Thunk (Dynamic ID)
export const deleteClient = createAsyncThunk(
  "admin/delete-client",
  async (id: number, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `admin/delete-client/${id}?type=1`,
      method: "delete",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data;
  },
);

// ✅ Get User List Thunk
export const getClientList = createAsyncThunk(
  "admin/get-client-list",
  async (
    {
      offset = 0,
      limit = 10,

      search = "",
    }: { offset?: number; limit?: number; role_id?: number; search: string },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");

    const queryParams = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    });
    if (search) {
      queryParams.append("search", search);
    }

    const response = await ApiHelperFunction({
      url: `admin/get-client-list/?${queryParams}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return {
      data: response?.data?.data || [],
      total_records: response?.data?.total_records || 0,
    };
  },
);

// ✅ Get Single User Thunk
export const getSingleClient = createAsyncThunk(
  "admin/get-client",
  async (id: number, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `admin/get-client/${id}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data?.data || {};
  },
);

export const activeInactiveClient = createAsyncThunk(
  "admin/active-inactive-client",
  async (id: number, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `admin/delete-client/${id}?type=0`,
      method: "delete",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data;
  },
);
export const ClientDetailData = createAsyncThunk(
  "admin/get-client/{user_id}",
  async (id: any, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `admin/get-client/${id}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data?.data || {};
  },
);

// ✅ Get Invoice List Thunk
export const getInvoiceList = createAsyncThunk(
  "/get-invoice-list",
  async (
    {
      offset = 0,
      search = "",
      filter_by,
    }: { offset?: number; search: string; filter_by?: number | number[] },
    { dispatch },
  ) => {
    try {
      const token = localStorage.getItem("user_token");
      const limit = 10;
      const queryParams = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
        search: search?.toString(),
      });
      // Only add filter_by if present and not empty
      if (Array.isArray(filter_by) && filter_by.length > 0) {
        filter_by.forEach((val) =>
          queryParams.append("filter_by", String(val)),
        );
      } else if (typeof filter_by === "number" && filter_by !== -1) {
        queryParams.append("filter_by", String(filter_by));
      }

      const response = await ApiHelperFunction({
        url: `/get-invoice-list/?${queryParams}`,
        method: "get",
        data: {
          search,
          filter_by,
        },
        dispatch,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `${token}` : "",
        },
      });
      return {
        data: response?.data?.data || [],
        total_records: response?.data?.data?.total_records || 0,
      };
    } catch (error) {
      return error;
    }
  },
);

export const getOperatingAgreementList = createAsyncThunk(
  "/get-operating-agreements-list/",
  async (
    payload: {
      page: number;
      limit: number;
    },
    { dispatch },
  ) => {
    try {
      const token = localStorage.getItem("user_token");

      const response = await ApiHelperFunction({
        url: `/get-operating-agreements-list/?offset=${payload.page}&limit=${payload?.limit}`,
        method: "get",
        dispatch,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `${token}` : "",
        },
      });

      return {
        data: response?.data?.data || [],
        total_records: response?.data?.total_records || 0,
      };
    } catch (error) {
      return error;
    }
  },
);

// ✅ Get Operating Agreement Detail Thunk
export const getOperatingAgreementDetail = createAsyncThunk(
  "/get-operating-agreement-details",
  async ({ order_id }: { order_id: number }, { dispatch }) => {
    try {
      const token = localStorage.getItem("user_token");

      const response = await ApiHelperFunction({
        url: `/get-operating-agreement-details?order_id=${order_id}`,
        method: "get",

        dispatch,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `${token}` : "",
        },
      });
      return response?.data?.data || [];
    } catch (error) {
      return error;
    }
  },
);

export const getSubscriptionsData = createAsyncThunk(
  "/subscriptions/summary/",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/subscriptions/summary/`,
      method: "get",

      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data?.data || [];
  },
);

export const deleteSubscriptions = createAsyncThunk(
  "/subscriptions/cancel/",
  async (
    {
      subscription_id,
      subscription_type,
    }: { subscription_id: string; subscription_type: string },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/subscriptions/cancel/`,
      method: "post",
      data: {
        subscription_type,
        subscription_id,
      },
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data?.data || [];
  },
);

// ✅ Create Checkout Session Thunk
export const createCheckoutSession = createAsyncThunk(
  "/create-checkout-session",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: "/create-checkout-session",
      method: "post",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data?.data ?? null;
  },
);

// ✅ Setup Invoice Account Thunk
export const setupInvoiceAccount = createAsyncThunk(
  "/setup-invoice-account",
  async (
    { secret_key, account_id }: { secret_key: string; account_id: string },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: "/setup-invoice-account/",
      method: "post",
      data: {
        secret_key,
        account_id,
      },
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

export const downloadFile = createAsyncThunk(
  "/download/",
  async ({ url }: { url: string }, { dispatch }) => {
    const key = toStorageKey(url);
    if (!key) {
      return { status: 0, message: "This file is not available yet." };
    }
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/download/?url=${encodeStorageKey(key)}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

// ✅ Retrieve Customer List Thunk
export const retrieveCustomerList = createAsyncThunk(
  "admin/retrieve-customer-list/",
  async (
    {
      offset = 0,
      limit = 10,
      search = "",
    }: { offset?: number; limit?: number; search?: string } = {},
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");
    const queryParams = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    });
    if (search) {
      queryParams.append("search", search);
    }
    const response = await ApiHelperFunction({
      url: `/retrieve-customer-list/`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return {
      data: response?.data?.data || [],
      total_records: response?.data?.total_records || 0,
    };
  },
);

// ✅ Create Customer Thunk
export const createCustomer = createAsyncThunk(
  "/create-customer",
  async ({ email, name }: { email: string; name: string }, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const payload = {
      email,
      name,
      address: {
        line1: "string",
        line2: "string",
        city: "string",
        state: "string",
        postal_code: "string",
        country: "US",
      },
    };
    const response = await ApiHelperFunction({
      url: "/create-customer/",
      method: "post",
      data: payload,
      dispatch,
      headers: {
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

// ✅ Create Invoice Thunk
export const createInvoice = createAsyncThunk(
  "/create-invoice",
  async (
    payload: {
      customer_id: string;
      customer_name: string;
      customer_email: string;
      invoice_date: string;
      save_to_draft: boolean;
      invoice_due_date: string;
      tax_rate: number;
      line_items: {
        item_name: string;
        description: string;
        unit_cost: number;
        quantity: number;
      }[];
      notes: string;
    },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: "/create-invoice/",
      method: "post",
      data: payload,
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

// ✅ Get Invoice Details Thunk
export const getInvoiceDetails = createAsyncThunk(
  "/get-invoice-details",
  async (invoice_id: string, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/get-invoice-details/${invoice_id}/`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

// NEW: Delete Invoice Thunk
export const deleteInvoice = createAsyncThunk(
  "/delete-invoice",
  async (invoice_id: string, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/delete-invoice/${invoice_id}/`,
      method: "delete",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

// ✅ Get Dashboard Data Thunk
export const getDashboardData = createAsyncThunk(
  "/get-dashboard-data",
  async (payload: { type: string }, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/get-dashboard-data/?type=${payload.type}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

// NEW: Update Invoice Item Thunk
export const updateInvoiceItem = createAsyncThunk(
  "/update-invoice-line-items",
  async (
    payload: {
      invoice_id: number;
      invoice_date: string;
      save_to_draft: boolean;
      invoice_due_date: string;
      tax_rate: number;
      line_items: {
        item_name: string;
        description: string;
        unit_cost: number;
        quantity: number;
      }[];
      notes: string;
    },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/update-invoice-line-items/`,
      method: "put",
      data: payload,
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

// NEW: Send Invoice Thunk
export const sendInvoice = createAsyncThunk(
  "/send-invoice",
  async (payload: { invoice_id: number }, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: "/send-invoice/",
      method: "post",
      data: payload,
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

// send stripe code
export const sendStripeCode = createAsyncThunk(
  "/setup-invoice-account-by-oauth",
  async (payload: { code: string; state: string | null }, { dispatch }) => {
    // const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/setup-invoice-account-by-oauth/?code=${payload.code}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: payload.state ? `${payload.state}` : "",
      },
    });
    return response?.data;
  },
);
//disconnect stripe account
export const disconnectStripe = createAsyncThunk(
  "/disconnect-stripe/",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: "/disconnect-stripe/",
      method: "post",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

// ✅ Get Bank Link Token
export const getLinkBankToken = createAsyncThunk(
  "/get-link-bank-token",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/create-link-token/`,
      method: "post",

      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data?.data || [];
  },
);

export const userBankToken = createAsyncThunk(
  "/get-link-bank-token",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/create-link-token/`,
      method: "post",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data?.data || [];
  },
);

export const userExchangeToken = createAsyncThunk(
  "/get-exchange-bank-token",
  async (payload: { public_token: string }, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/exchange-public-token/`,
      method: "post",
      data: payload,
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data?.data || [];
  },
);

export const getBanks = createAsyncThunk(
  "/get-bank",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/get-bank-accounts/`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data?.data || [];
  },
);

export const getTransactions = createAsyncThunk(
  "/get-transactions",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/get-transactions/`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data?.data || [];
  },
);

export const getTransactionsStatus = createAsyncThunk(
  "/get-transactions-status",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/check-transactions-status/`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data || [];
  },
);

export const buyPlan = createAsyncThunk(
  "/buy-plan-smart-bookkeeping",
  async ({ planId }: { planId: string }, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    // build form-data
    const formData = new FormData();
    formData.append("plan_id", planId);

    const response = await ApiHelperFunction({
      url: `/plaid/buy-plan-smart-bookkeeping/`,
      method: "post",
      data: formData,
      dispatch,
      headers: {
        Authorization: token ? `${token}` : "",
        // don't set Content-Type manually, let browser set it with boundary
      },
    });

    return response?.data || [];
  },
);

export const getTransactionsTableData = createAsyncThunk(
  "/get-yearly-transactions",
  async (
    payload: {
      page: number;
      limit: number;
      transaction_type: number;
      filter_by_category: number;
      start_date: string | undefined;
      end_date: string | undefined;
      search: string;
    },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");

    // Convert input dates to yyyy-mm-dd format if defined
    const formatDate = (dateStr?: string) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0"); // Months start at 0
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    const formattedStartDate = formatDate(payload.start_date);
    const formattedEndDate = formatDate(payload.end_date);

    const response = await ApiHelperFunction({
      url: `/plaid/get-yearly-transactions/?page=${payload.page}&limit=${payload?.limit}&transaction_type=${payload?.transaction_type}&search=${payload?.search}&filter_by_category=${payload?.filter_by_category}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return {
      data: response?.data?.data || [],
      total_records: response?.data?.data?.total_count || 0,
    };
  },
);

export const deleteTransactions = createAsyncThunk(
  "/delete-transactions",
  async (transaction_id: number, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/plaid/delete-transaction/?transaction_id=${transaction_id}`,
      method: "delete",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

export const getCategories = createAsyncThunk(
  "/get-book-keeping-categories",
  async (type: number, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/plaid/get-book-keeping-categories/?type=${type}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

export const updateCategories = createAsyncThunk(
  "/update-transactions-category",
  async (
    payload: { transaction_id: number; category_id: number },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/plaid/update-transaction-category/?transaction_id=${payload?.transaction_id}&category_id=${payload?.category_id}`,
      method: "put",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

export const fetchASync = createAsyncThunk(
  "/sync-new-transactions",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/plaid/sync-new-transactions/`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

export const getBalanceSheet = createAsyncThunk(
  "/get-balance-sheet",
  async (payload: { year: number | null }, { dispatch }) => {
    try {
      const token = localStorage.getItem("user_token");

      const response = await ApiHelperFunction({
        url: `/plaid/get-balance-sheet/?year=${payload?.year}`,
        method: "get",
        dispatch,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `${token}` : "",
        },
      });
      return response?.data || [];
    } catch (error) {
      return error;
    }
  },
);

export const getBanksLists = createAsyncThunk(
  "/plaid/get-connected-disconnected-banks/",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/get-connected-disconnected-banks/`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data || [];
  },
);

export const disconnectBank = createAsyncThunk(
  "/plaid/disconnect-bank/",
  async ({ bankId }: { bankId: string }, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/disconnect-bank/${bankId}`,
      method: "delete",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data || [];
  },
);

export const reconnectBank = createAsyncThunk(
  "/plaid/reconnect-bank/",
  async ({ bankId }: { bankId: string }, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/reconnect-bank/?plaid_item_id=${bankId}`,
      method: "post",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data || [];
  },
);

export const flagBanks = createAsyncThunk(
  "/plaid/flag-bank/",
  async ({ bankIds }: { bankIds: any }, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/flag-bank/`,
      method: "post",
      dispatch,
      data: {
        plaid_item_ids: bankIds,
      },
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data || [];
  },
);

// ✅ Get Bank Link Token
export const updateLinkBankToken = createAsyncThunk(
  "/plaid/create-update-link-token/",
  async ({ id }: { id: number }, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/create-update-link-token/${id}`,
      method: "post",

      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data?.data || [];
  },
);

export const getRulesList = createAsyncThunk(
  "/get-bookkeeping-rules/",
  async (
    payload: {
      page: number;
      limit: number;
    },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/get-bookkeeping-rules/?offset=${payload.page}&limit=${payload?.limit}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return {
      data: response?.data?.data || [],
      total_records: response?.data?.total_records || 0,
    };
  },
);

export const createRules = createAsyncThunk(
  "/plaid/create-bookkeeping-rule/",
  async (payload: any, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const requestData = {
      ...payload,
      value:
        typeof payload?.value === "string"
          ? payload.value.trim()
          : payload?.value,
    };

    // If rule_id is 0, remove it before sending
    if (requestData.rule_id === 0) {
      delete requestData.rule_id;
    }

    const response = await ApiHelperFunction({
      url: `/plaid/create-bookkeeping-rule/`,
      method: "post",
      dispatch,
      data: requestData,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data || [];
  },
);

export const getRuleByID = createAsyncThunk(
  "/plaid/bookkeeping-rule-detail/",
  async (rule_id: any, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/bookkeeping-rule-detail/?rule_id=${rule_id}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data || [];
  },
);

export const deleteRule = createAsyncThunk(
  "/plaid/delete-bookkeeping-rule/",
  async (rule_id: any, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/delete-bookkeeping-rule/${rule_id}/`,
      method: "delete",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data || [];
  },
);

export const getBookkeeperPlans = createAsyncThunk(
  "/plaid/get-plans-of-smart-bookkeeping/",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/plaid/get-plans-of-smart-bookkeeping/`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data || [];
  },
);

export const getInvoicePlans = createAsyncThunk(
  "/plaid/get-plans-for-invoice/",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/get-plans-for-invoice/`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data || [];
  },
);

export const buyInvoicePlan = createAsyncThunk(
  "/buy-plan-invoice",
  async (payload: { plan_id: string | null }, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const formData = new FormData();
    formData.append("plan_id", payload?.plan_id ? payload?.plan_id : "");

    const response = await ApiHelperFunction({
      url: `/buy-plan-invoice/`,
      method: "post",
      data: formData,
      dispatch,
      headers: {
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data || [];
  },
);

export const getCustomInvoices = createAsyncThunk(
  "/get-custom-invoices/",
  async (
    payload: { offset: number; limit: number; search: string },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/custom-invoices/?offset=${payload.offset}&limit=${payload.limit}&search=${payload.search}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data || [];
  },
);

export const createCustomInvoice = createAsyncThunk(
  "/create-custom-invoice",
  async (
    payload: {
      customer_country: string;
      customer_name: string;
      customer_email: string;
      invoice_date: string;
      save_to_draft: boolean;
      invoice_due_date: string;
      tax_rate: number;
      bank_details: string;
      total_amount: number;
      line_items: {
        item_name: string;
        description: string;
        unit_cost: number;
        quantity: number;
      }[];
      notes: string;
    },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: "/create-custom-invoice/",
      method: "post",
      data: payload,
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

export const updateCustomInvoice = createAsyncThunk(
  "/update-custom-invoice",
  async (
    payload: {
      invoice_id: string; // still required, but only for URL
      customer_country: string;
      customer_name: string;
      customer_email: string;
      invoice_date: string;
      save_to_draft: boolean;
      invoice_due_date: string;
      tax_rate: number;
      bank_details: string;
      total_amount: number;
      line_items: {
        item_name: string;
        description: string;
        unit_cost: number;
        quantity: number;
      }[];
      notes: string;
    },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");

    // destructure to exclude invoice_id from body
    const { invoice_id, ...rest } = payload;

    const response = await ApiHelperFunction({
      url: `/update-custom-invoice/${invoice_id}/`,
      method: "PUT",
      data: rest, // ✅ no invoice_id in body
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return response?.data;
  },
);

export const deleteCustomInvoice = createAsyncThunk(
  "/delete-custom-invoice",
  async (invoice_id: string, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/delete-custom-invoice/${invoice_id}/`,
      method: "delete",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

export const getCustomInvoiceDetails = createAsyncThunk(
  "/get-custom-invoice",
  async (invoice_id: string | undefined, { dispatch }) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/get-custom-invoice/${invoice_id}/`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

export const getClientsToolsList = createAsyncThunk(
  "/admin/get-client-tools",
  async (
    payload: {
      id: string;
      offset: number;
      limit: number;
    },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/admin/get-client-tools/${payload?.id}?offset=${payload.offset}&limit=${payload.limit}`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return {
      data: response?.data?.data || [],
      total_records: response?.data?.total_records || 0,
    };
  },
);

export const getallTools = createAsyncThunk(
  "/admin/get-all-tools",
  async (_, { dispatch }) => {
    const token = localStorage.getItem("user_token");

    const response = await ApiHelperFunction({
      url: `/admin/get-all-tools/`,
      method: "get",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });

    return {
      data: response?.data?.data || [],
      total_records: response?.data?.data?.total_records || 0,
    };
  },
);

export const assignTool = createAsyncThunk(
  "/admin/assign-tool",
  async (
    payload: {
      id: string;
      tool_id: number;
      date?: any;
    },
    { dispatch },
  ) => {
    try {
      const token = localStorage.getItem("user_token");

      const response = await ApiHelperFunction({
        url: `/admin/assign-tool/`,
        method: "post",
        data: {
          user_id: Number(payload?.id),
          tool_id: payload?.tool_id,
          free_trial_end_date: payload?.date,
        },
        dispatch,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `${token}` : "",
        },
      });

      return {
        data: response?.data?.data || [],
        message: response?.data?.message,
        code: response?.data?.code,
      };
    } catch (error) {
      return error;
    }
  },
);

export const deactivateCleintTool = createAsyncThunk(
  "/admin/deactivate-tool",
  async (
    { id, tool_type }: { id: number; tool_type: number },
    { dispatch },
  ) => {
    const token = localStorage.getItem("user_token");
    const response = await ApiHelperFunction({
      url: `/admin/deactivate-tool/${tool_type}/${id}`,
      method: "delete",
      dispatch,
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `${token}` : "",
      },
    });
    return response?.data;
  },
);

// ✅ Initial State
export const initialState: initialStateProps = {
  status: "idle",
  error: "",
  token: "",
  data: [],
  total_records: 0,
  records_limit: 10,
  clientDetail: [],
  detailLoading: "idle",
  clientListStatus: "idle",
  setupInvoiceAccountStatus: "idle",
  setupInvoiceAccountError: "",
  retrieveCustomerListStatus: "idle",
  retrieveCustomerListError: "",
  createCustomerStatus: "idle",
  createCustomerError: "",
  createInvoiceStatus: "idle",
  createInvoiceError: "",
  customerList: [],
};
export const clientSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    resetAll: (state) => {
      state.error = "";
      state.data = [];
      state.total_records = 0;
      state.records_limit = 10;
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Add User
      .addCase(addClient.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = [...state.data, action.payload?.user];
      })
      .addCase(addClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Update User
      .addCase(updateClient.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = [...state.data, action.payload?.user];
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Delete User
      .addCase(deleteClient.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.status = "succeeded";
        const deletedId = action.meta.arg;
        state.data = state.data.filter((user: any) => user.id !== deletedId);
      })
      .addCase(deleteClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Active/Inactive Agents (now using a unique action type)
      .addCase(activeInactiveClient.pending, (state) => {
        state.status = "loading";
      })

      .addCase(activeInactiveClient.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // ✅ Get User List
      .addCase(getClientList.pending, (state) => {
        state.clientListStatus = "loading";
      })
      .addCase(getClientList.fulfilled, (state, action) => {
        state.clientListStatus = "succeeded";
        state.data = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(getClientList.rejected, (state, action) => {
        state.clientListStatus = "failed";
        state.error = action.error.message;
      })
      .addCase(ClientDetailData.pending, (state) => {
        state.detailLoading = "loading";
        // state.status = "loading";
      })
      .addCase(ClientDetailData.fulfilled, (state, action) => {
        state.detailLoading = "succeeded";
        // state.status = "succeeded";
        state.clientDetail = action.payload;
        state.total_records = action.payload.total_records;
      })
      .addCase(ClientDetailData.rejected, (state, action) => {
        state.detailLoading = "failed";

        state.error = action.error.message;
      })
      .addCase(getInvoiceList.pending, (state) => {
        state.clientListStatus = "loading";
      })
      .addCase(getInvoiceList.fulfilled, (state, action: any) => {
        state.clientListStatus = "succeeded";
        state.data = action?.payload?.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(getInvoiceList.rejected, (state, action) => {
        state.clientListStatus = "failed";
        state.error = action.error.message;
      })
      .addCase(getTransactionsTableData.pending, (state) => {
        state.clientListStatus = "loading";
      })
      .addCase(getTransactionsTableData.fulfilled, (state, action) => {
        state.clientListStatus = "succeeded";
        state.data = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(getTransactionsTableData.rejected, (state, action) => {
        state.clientListStatus = "failed";
        state.error = action.error.message;
      })
      .addCase(setupInvoiceAccount.pending, (state) => {
        state.setupInvoiceAccountStatus = "loading";
        state.setupInvoiceAccountError = "";
      })
      .addCase(setupInvoiceAccount.fulfilled, (state) => {
        state.setupInvoiceAccountStatus = "succeeded";
        state.setupInvoiceAccountError = "";
      })
      .addCase(setupInvoiceAccount.rejected, (state, action) => {
        state.setupInvoiceAccountStatus = "failed";
        state.setupInvoiceAccountError =
          action.error.message || "Failed to setup account";
      })
      .addCase(retrieveCustomerList.pending, (state) => {
        state.retrieveCustomerListStatus = "loading";
        state.retrieveCustomerListError = "";
      })
      .addCase(retrieveCustomerList.fulfilled, (state, action) => {
        state.retrieveCustomerListStatus = "succeeded";
        state.retrieveCustomerListError = "";
        state.customerList = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(retrieveCustomerList.rejected, (state, action) => {
        state.retrieveCustomerListStatus = "failed";
        state.retrieveCustomerListError =
          action.error.message || "Failed to retrieve customer list";
      })
      // ✅ Create Customer
      .addCase(createCustomer.pending, (state) => {
        state.createCustomerStatus = "loading";
        state.createCustomerError = "";
      })
      .addCase(createCustomer.fulfilled, (state) => {
        state.createCustomerStatus = "succeeded";
        state.createCustomerError = "";
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.createCustomerStatus = "failed";
        state.createCustomerError =
          action.error.message || "Failed to create customer";
      })
      // ✅ Create Invoice
      .addCase(createInvoice.pending, (state) => {
        state.createInvoiceStatus = "loading";
        state.createInvoiceError = "";
      })
      .addCase(createInvoice.fulfilled, (state) => {
        state.createInvoiceStatus = "succeeded";
        state.createInvoiceError = "";
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.createInvoiceStatus = "failed";
        state.createInvoiceError =
          action.error.message || "Failed to create invoice";
      })
      .addCase(getCustomInvoices.pending, (state) => {
        state.clientListStatus = "loading";
      })
      .addCase(getCustomInvoices.fulfilled, (state, action) => {
        state.clientListStatus = "succeeded";
        state.data = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(getCustomInvoices.rejected, (state, action) => {
        state.clientListStatus = "failed";
        state.error = action.error.message;
      })
      .addCase(getClientsToolsList.pending, (state) => {
        state.clientListStatus = "loading";
      })
      .addCase(getClientsToolsList.fulfilled, (state, action) => {
        state.clientListStatus = "succeeded";
        state.data = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(getClientsToolsList.rejected, (state, action) => {
        state.clientListStatus = "failed";
        state.error = action.error.message;
      });
  },
});
